import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'framer-motion';
import { ChevronDown, Hash, Plus, Smile } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
// ---------------------------------------------------------------------------
// Data — a set of Slack-style "conversations" that each end with the team
// @mentioning Giga and Giga replying with a finished result. The hero cycles
// through them so visitors see several use cases, the way Viktor's reel does.
// ---------------------------------------------------------------------------
type ResultCard = {
  title: string;
  meta: string;
  stat: string;
  statLabel: string;
  bars: number[];
  fileName: string;
  fileMeta: string;
};
type Msg = {
  author: string;
  avatar: string;
  time: string;
  app?: boolean;
  mention?: string;
  text: string;
  card?: ResultCard;
  reactions?: {
    emoji: string;
    count: number;
  }[];
};
type Scenario = {
  channel: string;
  messages: Msg[];
};
const SCENARIOS: Scenario[] = [{
  channel: 'results',
  messages: [{
    author: 'Maya Chen',
    avatar: 'https://i.pravatar.cc/80?img=47',
    time: '8:01 AM',
    mention: '@Giga',
    text: ' pull the weekly numbers from Stripe, GA4, and HubSpot and post the recap.'
  }, {
    author: 'Jordan Ellis',
    avatar: 'https://i.pravatar.cc/80?img=12',
    time: '8:02 AM',
    text: 'flag anything that moved more than 10% week over week'
  }, {
    author: 'Giga',
    avatar: '',
    time: '8:06 AM',
    app: true,
    text: 'Done. Revenue, signups, CAC, and pipeline in one view. Paid signups up 23%, refunds down. Want me to run this every Monday at 8?',
    card: {
      title: 'Weekly performance',
      meta: 'Northwind · week of Jun 8',
      stat: '+23%',
      statLabel: 'paid signups w/w',
      bars: [40, 55, 38, 70, 48, 82, 60, 95, 72],
      fileName: 'Weekly-Performance.pdf',
      fileMeta: '6 pages · PDF'
    },
    reactions: [{
      emoji: '🎉',
      count: 5
    }, {
      emoji: '💯',
      count: 2
    }]
  }]
}, {
  channel: 'support',
  messages: [{
    author: 'Priya Nair',
    avatar: 'https://i.pravatar.cc/80?img=32',
    time: '9:14 AM',
    mention: '@Giga',
    text: ' the Acme account is escalating — can you pull their last 5 tickets and draft a reply?'
  }, {
    author: 'Tomás Rivera',
    avatar: 'https://i.pravatar.cc/80?img=15',
    time: '9:15 AM',
    text: 'include the refund we promised them last week'
  }, {
    author: 'Giga',
    avatar: '',
    time: '9:17 AM',
    app: true,
    text: 'Pulled all 5 tickets from Zendesk and the refund from Stripe. Drafted a reply that references both and offers a 2-month credit. Send it?',
    card: {
      title: 'Acme · escalation',
      meta: '5 tickets · 1 refund',
      stat: '4.9★',
      statLabel: 'CSAT after fix',
      bars: [30, 45, 35, 60, 52, 75, 68, 88, 80],
      fileName: 'Acme-Reply-Draft.txt',
      fileMeta: 'Ready to send'
    },
    reactions: [{
      emoji: '🙏',
      count: 3
    }, {
      emoji: '🔥',
      count: 4
    }]
  }]
}, {
  channel: 'growth',
  messages: [{
    author: 'Sofia Alvarez',
    avatar: 'https://i.pravatar.cc/80?img=5',
    time: '11:02 AM',
    mention: '@Giga',
    text: ' compare our top 7 paid suppliers and find where we’re overspending.'
  }, {
    author: 'Liam O’Brien',
    avatar: 'https://i.pravatar.cc/80?img=68',
    time: '11:03 AM',
    text: 'group it by channel so we can cut the worst one'
  }, {
    author: 'Giga',
    avatar: '',
    time: '11:06 AM',
    app: true,
    text: 'Compared all 7. One channel is 41% above the median CPA with no lift. Swapping budget to the top 2 saves ~$18k/mo. Want the breakdown?',
    card: {
      title: 'Spend audit',
      meta: '7 channels · last 30 days',
      stat: '-$18k',
      statLabel: 'projected monthly save',
      bars: [88, 72, 60, 95, 40, 30, 55, 48, 35],
      fileName: 'Spend-Audit.pdf',
      fileMeta: '4 pages · PDF'
    },
    reactions: [{
      emoji: '🤑',
      count: 6
    }, {
      emoji: '👏',
      count: 3
    }]
  }]
}];
const CHANNELS = ['results', 'general', 'finance', 'eng', 'growth', 'random'];
// ---------------------------------------------------------------------------
function GigaAvatar() {
  return <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#EA580C] shadow-sm" data-id="element-3505">
      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/0e427b37-c883-4901-a20e-16572a4d8a37/Vector.png" alt="Giga" className="h-4 w-4 object-contain [filter:brightness(0)_invert(1)]" data-id="element-3506" />
    </span>;
}
function ResultCardView({
  card
}: {
  card: ResultCard;
}) {
  return <div className="mt-2 w-full max-w-[320px] overflow-hidden rounded-2xl border border-[#F1E6DA] bg-white shadow-[0_8px_24px_rgba(43,33,26,0.08)]" data-id="element-3507">
      {/* warm gradient header with chart */}
      <div className="relative bg-gradient-to-br from-[#FBE3C7] via-[#F8C98E] to-[#EA580C]/70 p-4" data-id="element-3508">
        <div className="flex items-start justify-between" data-id="element-3509">
          <div data-id="element-3510">
            <p className="font-title text-lg font-semibold leading-tight text-[#2B211A]" data-id="element-3511">
              {card.title}
            </p>
            <p className="text-[11px] font-medium text-[#2B211A]/60" data-id="element-3512">
              {card.meta}
            </p>
          </div>
          <div className="text-right" data-id="element-3513">
            <p className="font-title text-2xl font-bold leading-none text-[#2B211A]" data-id="element-3514">
              {card.stat}
            </p>
            <p className="text-[10px] font-medium text-[#2B211A]/60" data-id="element-3515">
              {card.statLabel}
            </p>
          </div>
        </div>
        <div className="mt-4 flex h-12 items-end gap-1" data-id="element-3516">
          {card.bars.map((h, i) => <div key={i} className="flex-1 rounded-t-[3px] bg-white/55" style={{
          height: `${h}%`
        }} data-id="element-3517" />)}
        </div>
      </div>
      {/* file pill */}
      <div className="flex items-center gap-3 px-4 py-3" data-id="element-3518">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#FAF3EC] text-[11px] font-bold text-[#EA580C]" data-id="element-3519">
          PDF
        </span>
        <div className="min-w-0" data-id="element-3520">
          <p className="truncate text-sm font-semibold text-[#2B211A]" data-id="element-3521">
            {card.fileName}
          </p>
          <p className="text-[11px] text-neutral-400" data-id="element-3522">{card.fileMeta}</p>
        </div>
      </div>
    </div>;
}
function MessageRow({
  msg,
  index
}: {
  msg: Msg;
  index: number;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.45,
    ease: [0.22, 1, 0.36, 1],
    delay: index * 0.04
  }} className="flex gap-3 px-4 py-2.5 md:px-6" data-id="element-3523">
      {msg.app ? <GigaAvatar data-id="element-3524" /> : <img src={msg.avatar} alt={msg.author} className="h-9 w-9 shrink-0 rounded-[10px] object-cover" data-id="element-3525" />}
      <div className="min-w-0 flex-1" data-id="element-3526">
        <div className="flex items-center gap-2" data-id="element-3527">
          <span className="text-sm font-bold text-[#2B211A]" data-id="element-3528">{msg.author}</span>
          {msg.app && <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[9px] font-bold tracking-wide text-neutral-500" data-id="element-3529">
              APP
            </span>}
          <span className="text-[11px] text-neutral-400" data-id="element-3530">{msg.time}</span>
        </div>
        <p className="mt-0.5 text-sm leading-relaxed text-[#2B211A]" data-id="element-3531">
          {msg.mention && <span className="rounded bg-[#EA580C]/12 px-1 font-semibold text-[#C2410C]" data-id="element-3532">
              {msg.mention}
            </span>}
          {msg.text}
        </p>
        {msg.card && <ResultCardView card={msg.card} data-id="element-3533" />}
        {msg.reactions && <div className="mt-2 flex items-center gap-1.5" data-id="element-3534">
            {msg.reactions.map(r => <span key={r.emoji} className="flex items-center gap-1 rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-xs font-medium text-neutral-600" data-id="element-3535">
                <span data-id="element-3536">{r.emoji}</span>
                {r.count}
              </span>)}
            <span className="flex h-6 w-6 items-center justify-center rounded-full border border-neutral-200 bg-neutral-50 text-neutral-400" data-id="element-3537">
              <Smile size={13} data-id="element-3538" />
            </span>
          </div>}
      </div>
    </motion.div>;
}
export function WorkspaceHero() {
  const reduced = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    margin: '-15%',
    once: false
  });
  const [scenarioIndex, setScenarioIndex] = useState(0);
  const [revealed, setRevealed] = useState(reduced ? 99 : 0);
  const scenario = SCENARIOS[scenarioIndex];
  const total = scenario.messages.length;
  // Reveal messages one-by-one; once the conversation is complete, hold, then
  // advance to the next scenario. Pauses entirely when off-screen.
  useEffect(() => {
    if (reduced) {
      setRevealed(total);
      return;
    }
    if (!inView) return;
    if (revealed < total) {
      // Giga's reply (the last message) gets a longer "thinking" beat.
      const isGigaNext = revealed === total - 1;
      const id = setTimeout(() => setRevealed(r => r + 1), isGigaNext ? 1100 : 750);
      return () => clearTimeout(id);
    }
    // Conversation complete — hold, then swap to the next scenario.
    const id = setTimeout(() => {
      setScenarioIndex(i => (i + 1) % SCENARIOS.length);
      setRevealed(0);
    }, 3200);
    return () => clearTimeout(id);
  }, [inView, revealed, total, scenarioIndex, reduced]);
  const shown = scenario.messages.slice(0, reduced ? total : revealed);
  return <section className="relative w-full overflow-hidden bg-[#FAF8F4] pt-28 pb-16 md:pt-36 md:pb-24" data-id="element-3539">
      {/* soft warm wash */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(120% 70% at 50% 0%, rgba(234,88,12,0.07) 0%, rgba(250,248,244,0) 55%)'
    }} data-id="element-3540" />

      <div className="relative z-10 mx-auto max-w-[1100px] px-5 md:px-8" data-id="element-3541">
        {/* Marketing header */}
        <div className="mx-auto mb-12 max-w-2xl text-center md:mb-16" data-id="element-3542">
          <span className="mb-5 block text-xs font-semibold uppercase tracking-[5px] text-[#EA580C]" data-id="element-3543">
            Works where you work
          </span>
          <h1 className="font-title text-4xl font-medium leading-[1.05] tracking-tight text-[#2B211A] sm:text-5xl md:text-6xl" data-id="element-3544">
            A teammate in every{' '}
            <span className="italic font-light" data-id="element-3545">channel.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-[#6B5D52] md:text-lg" data-id="element-3546">
            @mention Giga in Slack or Teams. It reads your tools, does the work,
            and drops the finished result right where the conversation is.
          </p>
        </div>

        {/* Workspace window */}
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.6,
        ease: [0.22, 1, 0.36, 1]
      }} className="mx-auto overflow-hidden rounded-[20px] border border-neutral-200/80 bg-white shadow-[0_30px_80px_-20px_rgba(43,33,26,0.25)]" data-id="element-3547">
          {/* Top bar */}
          <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3 md:px-6" data-id="element-3548">
            <div className="flex items-center gap-2" data-id="element-3549">
              <span className="font-title text-base font-bold text-[#2B211A]" data-id="element-3550">
                Workspace
              </span>
              <ChevronDown size={15} className="text-neutral-400" data-id="element-3551" />
            </div>
            {/* Slack / Teams toggle */}
            <div className="flex items-center gap-1 rounded-full bg-neutral-100 p-1" data-id="element-3552">
              <span className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-neutral-500" data-id="element-3553">
                <img src="https://cdn.simpleicons.org/microsoftteams" alt="" aria-hidden="true" className="h-3.5 w-3.5" data-id="element-3554" />
                <span className="hidden sm:inline" data-id="element-3555">Teams</span>
              </span>
              <span className="flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#2B211A] shadow-sm" data-id="element-3556">
                <img src="https://cdn.simpleicons.org/slack" alt="" aria-hidden="true" className="h-3.5 w-3.5" data-id="element-3557" />
                Slack
              </span>
            </div>
          </div>

          <div className="flex" data-id="element-3558">
            {/* Sidebar */}
            <aside className="hidden w-52 shrink-0 border-r border-neutral-100 bg-[#FCFAF7] p-4 md:block" data-id="element-3559">
              <div className="mb-2 flex items-center gap-1 text-xs font-semibold text-neutral-500" data-id="element-3560">
                <ChevronDown size={13} data-id="element-3561" />
                Channels
              </div>
              <ul className="space-y-0.5" data-id="element-3562">
                {CHANNELS.map(c => {
                const active = c === scenario.channel;
                return <li key={c} data-id="element-3563">
                      <span className={`flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm transition-colors ${active ? 'bg-[#EA580C]/10 font-semibold text-[#2B211A]' : 'text-neutral-500'}`} data-id="element-3564">
                        <Hash size={13} className={active ? 'text-[#EA580C]' : 'text-neutral-400'} data-id="element-3565" />
                        {c}
                      </span>
                    </li>;
              })}
              </ul>
              {/* skeleton rows */}
              <div className="mt-4 space-y-2.5 pl-1" data-id="element-3566">
                {[60, 80, 50, 70, 45].map((w, i) => <div key={i} className="flex items-center gap-2" data-id="element-3567">
                    <div className="h-2 w-2 rounded-sm bg-neutral-200" data-id="element-3568" />
                    <div className="h-2 rounded bg-neutral-200" style={{
                  width: `${w}%`
                }} data-id="element-3569" />
                  </div>)}
              </div>
            </aside>

            {/* Thread */}
            <div className="flex min-h-[440px] flex-1 flex-col" data-id="element-3570">
              {/* channel header */}
              <div className="flex items-center gap-1.5 border-b border-neutral-100 px-4 py-3 md:px-6" data-id="element-3571">
                <Hash size={16} className="text-neutral-400" data-id="element-3572" />
                <span className="font-title text-base font-bold text-[#2B211A]" data-id="element-3573">
                  {scenario.channel}
                </span>
                <ChevronDown size={14} className="text-neutral-400" data-id="element-3574" />
              </div>

              {/* messages */}
              <div className="flex-1 py-3" data-id="element-3575">
                <AnimatePresence mode="wait" data-id="element-3576">
                  <motion.div key={scenarioIndex} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.3
                }} data-id="element-3577">
                    {shown.map((msg, i) => <MessageRow key={i} msg={msg} index={i} data-id="element-3578" />)}
                    {/* typing indicator while Giga "thinks" */}
                    {!reduced && revealed === total - 1 && <div className="flex items-center gap-2 px-4 py-2 md:px-6" data-id="element-3579">
                        <GigaAvatar data-id="element-3580" />
                        <div className="flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-2" data-id="element-3581">
                          {[0, 1, 2].map(d => <motion.span key={d} className="h-1.5 w-1.5 rounded-full bg-neutral-400" animate={{
                        opacity: [0.3, 1, 0.3]
                      }} transition={{
                        duration: 1,
                        repeat: Infinity,
                        delay: d * 0.18
                      }} data-id="element-3582" />)}
                        </div>
                      </div>}
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* composer */}
              <div className="border-t border-neutral-100 p-3 md:p-4" data-id="element-3583">
                <div className="flex items-center gap-2 rounded-xl border border-neutral-200 px-3 py-2.5" data-id="element-3584">
                  <Plus size={16} className="text-neutral-400" data-id="element-3585" />
                  <span className="flex-1 text-sm text-neutral-400" data-id="element-3586">
                    Message #{scenario.channel}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <div className="mt-10 flex flex-col items-center gap-4 md:mt-12" data-id="element-3587">
          <WaitlistCTA className="inline-flex items-center justify-center rounded-full bg-[#EA580C] px-8 py-4 text-base font-bold text-white shadow-lg shadow-[#EA580C]/20 transition-all duration-300 hover:scale-105 hover:bg-[#d24e09]" data-id="element-3588">
            Add Giga to your workspace
          </WaitlistCTA>
          <p className="text-xs text-neutral-400" data-id="element-3589">
            Live in Slack or Teams in two minutes — no setup project required.
          </p>
        </div>
      </div>
    </section>;
}